import { AlertStatus, UseToastOptions } from '@chakra-ui/react';
import { UseMutationResult } from 'react-query';

import { AppError } from '../../framework/models/errors';
import { TypeUtils } from '../../framework/utils/UtilityTypes';
import {
  DownloadAttachedFileQuery,
  DownloadAttachedResult,
} from '../hooks/queries/attachedFile';
import { FileUtils } from '../utils/FileUtils';
import { FileKind } from './gen/fileKind';

type UrlType = 'view' | 'download' | 'preview';

/**
 * SE 外注 ファイルオブジェクト
 */
export class EngeneFileObject {
  /**
   * ファイル実体
   */
  file?: Blob;
  /**
   * ファイル ID
   */
  fileId?: number;
  /**
   * ファイル名
   */
  fileName: string;
  /**
   * ファイルサイズ [Bytes]
   */
  fileSize?: number;
  /**
   * ファイル種別
   */
  mode?: FileKind;
  /**
   * 新規ファイル名
   */
  newFileName?: string;
  /**
   * プレビュー用ファイル
   */
  previewFile?: Blob;
  toPreview = false;
  /**
   * ダウンロード URL
   *
   * @deprecated not used
   */
  url?: string;

  /**
   * コンストラクタ
   *
   * @param fileName -
   * @param fileSize -
   */
  constructor(fileName: string, fileSize?: number, fileId?: number) {
    Object.setPrototypeOf(this, new.target.prototype);
    this.fileName = fileName;
    this.fileId = fileId;
    this.fileSize = fileSize;
  }

  get formData() {
    const formData = new FormData();
    if (TypeUtils.isNonNil<Blob>(this.file)) {
      formData.append('files', this.file, this.fileName);
    }
    return formData;
  }

  /**
   * Gets whether has file
   */
  get hasFile() {
    return TypeUtils.isNonNil(this.file);
  }

  /**
   * Gets whether is uploaded
   */
  get isUploaded() {
    return TypeUtils.isNonNilNumber(this.fileId);
  }

  /**
   * Gets object url
   */
  get objectUrl() {
    return this.toPreview && TypeUtils.isNonNil(this.previewFile)
      ? URL.createObjectURL(this.previewFile)
      : this.hasFile
      ? URL.createObjectURL(this.file!)
      : undefined;
  }

  /**
   * Gets query
   */
  get query(): DownloadAttachedFileQuery {
    return { mode: this.mode, id: this.fileId, filename: this.fileName };
  }

  /**
   *
   * @param mode -
   * @returns
   */
  download = (
    caller: UseMutationResult<
      void | DownloadAttachedResult,
      unknown,
      DownloadAttachedFileQuery,
      unknown
    >,
    mode?: FileKind
  ) => {
    if (TypeUtils.isNonNil(mode)) {
      this.mode = mode;
    }
    if (TypeUtils.isNil(this.mode)) {
      return;
    }

    caller.mutate(this.query, {
      onSuccess: (result) => {
        if (TypeUtils.isNonNil<DownloadAttachedResult>(result)) {
          this.file = result.file;
        }
      },
    });
  };

  /**
   * ダウンロードしたファイルから生成する
   *
   * @param file -
   * @param fileId -
   * @param fileName -
   * @param mode -
   * @returns
   */
  static fromDownloaded = (
    file: Blob,
    fileId: number,
    fileName: string,
    mode: FileKind
  ) => {
    const obj = new EngeneFileObject(fileName, file.size, fileId);
    obj.file = file;
    obj.mode = mode;

    return obj;
  };

  /**
   *
   * @param file -
   * @returns
   */
  static fromFile = (file: File) => {
    const obj = new EngeneFileObject(file.name, file.size);
    obj.file = file;

    return obj;
  };

  static fromObject = (instance: unknown) => {
    if (this.isInstance(instance)) {
      try {
        const obj = new EngeneFileObject(instance.fileName);
        Object.assign(obj, instance);

        return obj;
      } catch (error) {
        return undefined;
      }
    }
    return undefined;
  };

  /**
   * 選択したファイルから生成する
   *
   * @param file -
   * @param prefix -
   * @param index -
   * @returns
   */
  static fromSelected = (file: File, prefix: string, index: number) => {
    const obj = new EngeneFileObject(file.name, file.size);
    obj.file = file;
    obj.newFileName = EngeneFileObject.generateNewFileName(
      prefix,
      // file.name.split('.').reverse()[0],
      'pdf',
      index
    );

    obj.judgePreview();

    return obj;
  };

  /**
   * 選択したファイルから生成し，プレビューファイルを生成する (pdf)
   *
   * @param file -
   * @param prefix -
   * @param index -
   * @returns
   */
  static fromSelectedAndGet = (
    file: File,
    prefix: string,
    index: number,
    caller: UseMutationResult<
      void | DownloadAttachedResult,
      unknown,
      FormData,
      unknown
    >
  ) => {
    const obj = new EngeneFileObject(file.name, file.size);
    obj.file = file;
    obj.newFileName = EngeneFileObject.generateNewFileName(
      prefix,
      // file.name.split('.').reverse()[0],
      'pdf',
      index
    );

    obj.judgePreview();
    obj.preview(caller);

    return obj;
  };

  /**
   * アップロード済ファイル情報から生成する
   *
   * @param fileId -
   * @param fileName -
   * @param fileSize -
   * @param mode -
   * @param type -
   * @returns
   */
  static fromUploadedInfoAnd = (
    fileId: number,
    fileName: string,
    mode: FileKind,
    type: UrlType,
    toast: BiConsumer<
      string | AppError,
      AlertStatus | UseToastOptions | undefined
    >,
    downloadCaller?: UseMutationResult<
      void | DownloadAttachedResult,
      unknown,
      DownloadAttachedFileQuery,
      unknown
    >,
    viewCaller?: UseMutationResult<
      void | DownloadAttachedResult[],
      unknown,
      DownloadAttachedFileQuery[],
      unknown
    >,
    fileSize?: number
  ) => {
    const obj = new EngeneFileObject(fileName, fileSize, fileId);
    obj.mode = mode;

    if (type === 'download' && TypeUtils.isNonNil(downloadCaller)) {
      obj.download(downloadCaller);
    } else if (type === 'view' && TypeUtils.isNonNil(viewCaller)) {
      obj.get(viewCaller, toast);
    }

    return obj;
  };

  /**
   * アップロード済ファイル情報から生成する
   *
   * @param infos -
   * @param mode -
   * @returns
   * @deprecated not work
   */
  static fromUploadedInfosAndGet = (
    infos: Array<{ fileId: number; fileName: string; fileSize?: number }>,
    mode: FileKind,
    caller: UseMutationResult<
      void | DownloadAttachedResult[],
      unknown,
      DownloadAttachedFileQuery[],
      unknown
    >
  ) => {
    const objs = infos.map((info) => {
      const obj = new EngeneFileObject(
        info.fileName,
        info.fileSize,
        info.fileId
      );
      obj.mode = mode;

      return obj;
    });

    this.getAll(objs, caller);

    return objs;
  };

  /**
   *
   * @param prefix -
   * @param suffix -
   * @param index -
   * @returns
   */
  static generateNewFileName = (
    prefix: string,
    suffix: string,
    index: number
  ) => {
    return `${prefix}_${index}.${suffix}`;
  };

  /**
   *
   * @param mode -
   * @returns
   */
  get = (
    caller: UseMutationResult<
      void | DownloadAttachedResult[],
      unknown,
      DownloadAttachedFileQuery[],
      unknown
    >,
    toast: BiConsumer<
      string | AppError,
      AlertStatus | UseToastOptions | undefined
    >,
    mode?: FileKind
  ) => {
    if (TypeUtils.isNonNil(mode)) {
      this.mode = mode;
    }
    if (TypeUtils.isNil(this.mode)) {
      return;
    }

    caller.mutate([this.query], {
      onSuccess: (results) => {
        if (TypeUtils.isNonNilArray(results)) {
          this.file = results[0].file;
          this.fileSize = results[0].file.size;
        }
      },
      onError: (error) =>
        error instanceof AppError
          ? error.status === 400
            ? toast('閲覧できないファイル種別です', 'warning')
            : toast(error, undefined)
          : toast('ファイルダウンロードに失敗しました', 'warning'),
    });
  };

  /**
   *
   * @param objs -
   * @param mode -
   * @returns
   */
  static getAll = async (
    objs: Array<EngeneFileObject>,
    caller: UseMutationResult<
      void | DownloadAttachedResult[],
      unknown,
      DownloadAttachedFileQuery[],
      unknown
    >,
    mode?: FileKind
  ) => {
    if (TypeUtils.isNonNil(mode)) {
      objs.forEach((obj) => {
        obj.mode = mode;
      });
    }
    if (objs.some((obj) => TypeUtils.isNil(obj.mode))) {
      return;
    }

    await caller.mutateAsync(
      objs.map((obj) => obj.query),
      {
        onSuccess: (results) => {
          if (TypeUtils.isNonNilArray(results)) {
            results.forEach((result, index) => {
              objs[index].file = result.file;
              objs[index].fileSize = result.file.size;
            });
          }
        },
      }
    );

    return objs;
  };

  /**
   * URL を自動生成する
   *
   * @param type -
   * @param mode -
   * @returns
   */
  getUrl = (type: UrlType, mode?: FileKind) => {
    if (TypeUtils.isNonNil(mode)) {
      this.mode = mode;
    }
    if (TypeUtils.isNil(this.mode)) {
      return undefined;
    }
    switch (type) {
      case 'download':
        return FileUtils.toFileDownloadUrl(this.query);
      case 'preview':
        // not GET method
        return undefined;
      case 'view':
        return FileUtils.toFileGetUrl(this.query);
    }
  };

  /**
   *
   * @param instance -
   * @returns
   */
  static isInstance = (instance: unknown): instance is EngeneFileObject => {
    return (
      instance instanceof EngeneFileObject ||
      (typeof instance === 'object' &&
        TypeUtils.isNonNil(instance) &&
        'fileName' in instance &&
        'toPreview' in instance)
    );
  };

  /**
   *
   */
  judgePreview = () => {
    if (this.hasFile && this.file?.type === 'application/pdf') {
      this.toPreview = true;
    }
  };

  /**
   *
   * @param mode -
   * @returns
   */
  preview = (
    caller: UseMutationResult<
      void | DownloadAttachedResult,
      unknown,
      FormData,
      unknown
    >,
    formData?: FormData
  ) =>
    caller.mutate(formData || this.formData, {
      onSuccess: (result) => {
        if (TypeUtils.isNonNil<DownloadAttachedResult>(result)) {
          this.previewFile = result.file;
        }
      },
    });

  /**
   *
   * @param prefix -
   * @param index -
   */
  setNewFileName = (prefix: string, index: number) => {
    console.debug("old 'newFileName':", this.newFileName);
    this.newFileName = EngeneFileObject.generateNewFileName(
      prefix,
      // this.fileName.split('.').reverse()[0],
      'pdf',
      index
    );
    console.debug("new 'newFileName':", this.newFileName);
  };
}
